// Tracks how many HTTP requests are currently in flight (fed by useHttp) plus a
// "slow" flag that the backend's early cache:slow-query WebSocket signal sets.
// The RequestActivityBar subscribes to render a thin top progress bar: it only
// appears once a request takes a moment (so fast cache hits never flash it) and
// turns amber while the slow flag is set.

const listeners = new Set();

let active = 0;
let slow = false;
// True while the backend served cached data and is reconciling it with the
// database in the background (X-FZ-Cache: stale). Auto-expires so the bar never
// sticks if no follow-up signal arrives.
let revalidating = false;
let revalidatingTimer = null;

const notify = () => {
  const snapshot = { active, slow, revalidating };
  for (const listener of listeners) listener(snapshot);
};

// A request started.
export const beginRequest = () => {
  active += 1;
  notify();
};

// A request finished (success or error). Once nothing is in flight, the slow
// flag resets so the next idle period starts clean.
export const endRequest = () => {
  active = Math.max(0, active - 1);
  if (active === 0) slow = false;
  notify();
};

// The backend signalled that the current work has to query the database.
export const markSlowRequest = () => {
  if (slow) return;
  slow = true;
  notify();
};

// The backend served stale cache data and is reconciling with the database in the
// background. Shows the reconcile state on the bar for a short window.
export const markRevalidating = (durationMs = 4000) => {
  revalidating = true;
  notify();
  if (revalidatingTimer) clearTimeout(revalidatingTimer);
  revalidatingTimer = setTimeout(() => {
    revalidating = false;
    revalidatingTimer = null;
    notify();
  }, durationMs);
};

export const subscribeRequestActivity = listener => {
  listeners.add(listener);
  listener({ active, slow });
  return () => listeners.delete(listener);
};
